import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Upload, History, FileText, ArrowRight } from "lucide-react";
import { scAmcLogout, scAmcMe, scAmcDashboard } from "../../lib/sifcontrolApi";

const Tile = ({ label, value, sub, testId }) => (
    <div className="rounded-2xl border border-oth-line bg-white p-5" data-testid={testId}>
        <div className="text-[10px] uppercase tracking-[0.2em] text-oth-ink3 font-body">{label}</div>
        <div className="mt-1 font-display text-2xl md:text-3xl font-semibold tracking-tight text-oth-ink">{value}</div>
        {sub && <div className="mt-1 text-[11px] font-body text-oth-ink3">{sub}</div>}
    </div>
);

const STATUS_LABEL = {
    draft: "Draft",
    validated: "Validated",
    pending_admin: "Pending review",
    approved: "Approved",
    rejected: "Rejected",
};

const fmt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
};

export default function SifControlAmcDashboard() {
    const [me, setMe] = useState(null);
    const [dash, setDash] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const load = async () => {
        try {
            const [meData, dashData] = await Promise.all([scAmcMe(), scAmcDashboard()]);
            setMe(meData);
            setDash(dashData);
        } catch {
            navigate("/sifcontrol/amc/login");
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const logout = async () => {
        try { await scAmcLogout(); } catch { /* ignore */ }
        toast.success("Signed out.");
        navigate("/sifcontrol/amc/login");
    };

    if (loading) return <div className="p-16 text-center text-sm text-oth-ink3">Loading…</div>;
    if (!me) return null;

    const last = dash?.last_upload;

    return (
        <section className="py-14 md:py-20" data-testid="sc-amc-dashboard">
            <div className="mx-auto max-w-[1240px] px-4 md:px-6">
                <div className="flex items-start justify-between gap-6 flex-wrap">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.22em] text-oth-ink3 font-body">AMC portal</div>
                        <h1 className="mt-1 font-display text-3xl md:text-4xl font-semibold tracking-tight text-oth-ink">
                            {me.amc?.amc_name || "Dashboard"}
                        </h1>
                        <div className="mt-1 text-sm font-body text-oth-ink2">Signed in as {me.user?.email}</div>
                    </div>
                    <button
                        onClick={logout}
                        className="inline-flex items-center gap-2 text-sm font-body text-oth-ink3 hover:text-oth-ink"
                        data-testid="sc-amc-logout"
                    >
                        <LogOut size={14} /> Sign out
                    </button>
                </div>

                <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Tile label="My SIFs" value={dash?.total_sifs ?? "—"} testId="sc-amc-tile-sifs" />
                    <Tile label="Pending review" value={dash?.pending_uploads ?? 0} sub="Uploads awaiting admin approval" testId="sc-amc-tile-pending" />
                    <Tile label="Approved uploads" value={dash?.approved_uploads ?? 0} sub="Total lifetime" testId="sc-amc-tile-approved" />
                </div>

                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Link
                        to="/sifcontrol/amc/upload"
                        className="rounded-3xl border border-oth-line bg-white p-6 hover:shadow-lg transition-shadow group"
                        data-testid="sc-amc-action-upload"
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-[#0A0A0A] text-white flex items-center justify-center">
                                <Upload size={18} />
                            </div>
                            <div>
                                <div className="font-display text-lg font-semibold text-oth-ink">Upload SIF details</div>
                                <div className="text-[12px] font-body text-oth-ink3">Update fund manager, benchmark, factsheet URL and more via CSV.</div>
                            </div>
                            <ArrowRight size={16} className="ml-auto text-oth-ink3 group-hover:text-oth-ink transition-colors" />
                        </div>
                    </Link>
                    <Link
                        to="/sifcontrol/amc/uploads"
                        className="rounded-3xl border border-oth-line bg-white p-6 hover:shadow-lg transition-shadow group"
                        data-testid="sc-amc-action-history"
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-white border border-oth-line text-oth-ink flex items-center justify-center">
                                <History size={18} />
                            </div>
                            <div>
                                <div className="font-display text-lg font-semibold text-oth-ink">Upload history</div>
                                <div className="text-[12px] font-body text-oth-ink3">See every submission, review status and per-row change log.</div>
                            </div>
                            <ArrowRight size={16} className="ml-auto text-oth-ink3 group-hover:text-oth-ink transition-colors" />
                        </div>
                    </Link>
                </div>

                {last && (
                    <div className="mt-8 rounded-3xl border border-oth-line bg-white p-6" data-testid="sc-amc-last-upload">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-oth-ink3 font-body">Most recent upload</div>
                        <div className="mt-2 flex items-center gap-3 flex-wrap">
                            <FileText size={16} className="text-oth-ink3" />
                            <span className="font-body text-oth-ink">{last.filename}</span>
                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-oth-bg-2 text-oth-ink3 font-body uppercase tracking-wider">
                                {STATUS_LABEL[last.status] || last.status}
                            </span>
                            <span className="text-[11px] font-body text-oth-ink3">{fmt(last.created_at)}</span>
                            <Link to={`/sifcontrol/amc/uploads/${last.id}`} className="ml-auto text-[11px] underline hover:text-oth-ink">
                                View details
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}
